import type RootStore from "stores/RootStore";
import type { RouteType } from "types/RouteType";
import type PeopleStore from "./PeopleStore";
import { gatePeopleRoute } from "./PeopleAccessStore";

/**
 * Administration → People (delegated user administration).
 *
 * Offered to administrators (who grant people administration here) and to the
 * people they grant it to (who administer the people below them here). It is
 * NOT `requiresAdmin`: the gate (PeopleAccessStore.ts) hides it from everyone
 * else and refuses entry. That gate is a convenience — every action on this
 * page is decided by the API's delegation guard, whatever the page offers.
 */

const storeOf = (rootStore: RootStore) => rootStore.peopleStore as unknown as PeopleStore;

/**
 * A failed write THROWS, so the dialog stays open with the API's reason in its
 * footer (the dialog framework keeps a dialog open when onSubmit rejects).
 */
const submitOrThrow = async (
  rootStore: RootStore,
  submit: (store: PeopleStore) => Promise<boolean>,
  errorOf: (store: PeopleStore) => string | null,
  fallback: string,
) => {
  const store = storeOf(rootStore);
  if (!(await submit(store))) throw new Error(errorOf(store) || fallback);
};

const peopleRoute: RouteType = gatePeopleRoute({
  name: "People",
  title: "People",
  path: "/people",
  layout: "/app",
  viewportCard: {
    header: {
      title: "People",
      component: () => import("./People.Header"),
      enableRefreshButton: true,
      enableBackButton: true,
    },
    body: {
      component: () => import("./People.Body"),
    },
  },
  group: "Admin",
  searchAliases: ["people administrator", "team lead", "delegated access", "approve sign-in", "add person"],
  hidden: false,
  requiresAuth: true,
  requiresPermission: false,
  requiresAdmin: false,
  enableAutoFind: false,
  onEnter: async ({ rootStore }) => {
    void storeOf(rootStore)?.load();
  },
  dialogs: [
    {
      name: "PersonDialog",
      title: "Person",
      component: () => import("./dialogs/PersonDialog.Body"),
      cancelButton: true,
      maxWidth: "sm",
      height: 560,
      submitButtonText: "Save",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(rootStore, (s) => s.savePerson(), (s) => s.personError, "The change was not made."),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => storeOf(rootStore).isPersonFormValid,
    },
    {
      name: "DeactivatePersonDialog",
      title: "Deactivate account",
      component: () => import("./dialogs/DeactivatePersonDialog.Body"),
      cancelButton: true,
      maxWidth: "xs",
      height: 300,
      submitButtonText: "Deactivate",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(rootStore, (s) => s.submitDeactivate(), (s) => s.deactivateError, "The account was not deactivated."),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const store = storeOf(rootStore);
        return !!store.deactivateTarget && !store.deactivating;
      },
    },
    {
      // Adds and removals happen inside the dialog, one at a time; Done closes it.
      name: "PersonClientsDialog",
      title: "Clients",
      component: () => import("./dialogs/PersonClientsDialog.Body"),
      cancelButton: false,
      maxWidth: "sm",
      height: 520,
      submitButtonText: "Done",
      onSubmit: async () => undefined,
    },
    {
      name: "DelegationDialog",
      title: "People administrator",
      component: () => import("./dialogs/DelegationDialog.Body"),
      cancelButton: true,
      maxWidth: "sm",
      height: 600,
      submitButtonText: "Save",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(rootStore, (s) => s.saveDelegation(), (s) => s.delegationError, "The people administrator was not saved."),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => storeOf(rootStore).canSaveDelegation,
    },
    {
      name: "RevokeDelegationDialog",
      title: "Revoke people administration",
      component: () => import("./dialogs/RevokeDelegationDialog.Body"),
      cancelButton: true,
      maxWidth: "xs",
      height: 300,
      submitButtonText: "Revoke",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        submitOrThrow(rootStore, (s) => s.submitRevoke(), (s) => s.revokeError, "People administration was not revoked."),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const store = storeOf(rootStore);
        return !!store.revokeTarget && !store.revoking;
      },
    },
  ],
});

export default peopleRoute;
